import { useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { loadOverlays, reportOverlayFailure, useOverlays } from '../../components/DeferredOverlay';
import { isEditableTarget } from '../search/hotkey';
import { CHORD_TIMEOUT_MS, globalKeyIntent } from './goKeys';
import { inBlockingDialog } from './dom';
import { setShortcutsOpen, useShortcutsState } from './pageShortcuts';
import { singleKeysEnabled, useSingleKeysEnabled } from './singleKeys';

/**
 * The application's keys, wired to the document, and the one `?` list.
 *
 * MOUNTED ONCE, IN THE SHELL, beside `UniversalSearch` and for its reason: it has to outlive every
 * route change. The decisions are `globalKeyIntent` in ./goKeys, where they are tested.
 *
 * CAPTURE PHASE, AHEAD OF THE PAGE. The review queue and the arrears register handle keys on a
 * focused list; once `g` is down, the next key is this layer's, and the only way to keep it from
 * the page is to see it first and stop it there. A page therefore never sees `g`, `?`, or the key
 * after `g` — which is the contract a page's own key map is written against.
 *
 * THE KEYS ARE EAGER; ONLY THE LIST IS LATE. `g o` has to work from the first second, and costs
 * almost nothing. The list is a Radix Dialog, so it is in the shell's overlays chunk — see
 * `DeferredOverlay`, and `ShortcutList`.
 */
export function ShortcutKeys() {
  const navigate = useNavigate();
  const { open } = useShortcutsState();
  const overlays = useOverlays();
  // A ref, not state: whether `g` is waiting changes nothing on screen, and a re-render per
  // keystroke would be paid for nothing.
  const armedUntil = useRef(0);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;

      const intent = globalKeyIntent({
        key: event.key,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        altKey: event.altKey,
        repeat: event.repeat,
        isEditable: isEditableTarget(event.target),
        inDialog: inBlockingDialog(event.target),
        armed: event.timeStamp < armedUntil.current,
        singleKeys: singleKeysEnabled(),
      });
      if (intent.kind === 'ignore') return;

      event.preventDefault();
      event.stopPropagation();

      switch (intent.kind) {
        case 'arm':
          armedUntil.current = event.timeStamp + CHORD_TIMEOUT_MS;
          return;
        case 'go':
          armedUntil.current = 0;
          setShortcutsOpen(false);
          // Through the router, so the unsaved-changes guard asks exactly as it does for a click
          // on the rail — `g` from a half-filled form is not a way to lose it.
          void navigate({ to: intent.to });
          return;
        case 'cancel':
          armedUntil.current = 0;
          return;
        case 'toggleHelp':
          setShortcutsOpen((open) => !open);
          return;
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [navigate]);

  // `?` pressed before the overlays chunk has landed: ask for it now. If it cannot arrive, close
  // again with a notice rather than staying "open" with nothing on screen — as help does.
  useEffect(() => {
    if (!open || overlays) return;
    loadOverlays().catch(() => {
      setShortcutsOpen(false);
      reportOverlayFailure('Could not open keyboard shortcuts');
    });
  }, [open, overlays]);

  return overlays ? <overlays.ShortcutList /> : null;
}

/**
 * The quiet link at the foot of a keyboard-driven page, so somebody who has never pressed `?` can
 * still find out that there is something to press. Opens the shell's list — there is no other.
 *
 * Hidden below `md`: a phone has no keys to press.
 */
export function ShortcutsLink({ className = '' }: { className?: string }) {
  // `(?)` only while `?` works — a hint at a key that does nothing is worse than none.
  const singleKeys = useSingleKeysEnabled();

  return (
    <button
      type="button"
      aria-keyshortcuts={singleKeys ? 'Shift+?' : undefined}
      onClick={() => setShortcutsOpen(true)}
      className={`hidden w-fit text-label text-ink-subtle underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--focus)] focus-visible:outline-none md:block ${className}`}
    >
      {singleKeys ? 'Keyboard shortcuts (?)' : 'Keyboard shortcuts'}
    </button>
  );
}
